import type { WorkbookDefinition } from "@erp/shared-types";
import { lexFormula, type TableName } from "./formulaLexer";

/** A Source or Worksheet, which a Table reference names. */
export interface WorkbookTableItem {
  id: string;
  name: string;
  /**
   * Its columns' names in their current order, when known: a freeform
   * reference stored by column name is shown by its column's letter here.
   */
  columns?: readonly string[];
}

/** Each table's columns by id, as a Plan's schemas (`WorkbookPlan.tables`) hold them. */
export type TableColumns = ReadonlyMap<string, readonly { name: string }[]>;

export type TableResolution =
  | { ok: true; item: WorkbookTableItem }
  /**
   * `missing`: a stored id whose Item was removed; `unknown`: no table has the
   * name; `ambiguous`: more than one does, ignoring case.
   */
  | { ok: false; reason: "missing" | "unknown" | "ambiguous" };

/**
 * Every table a Table reference can name: the Sources, then the Worksheets,
 * each with its columns when `columns` has them.
 */
export function tableItems(
  definition: Pick<WorkbookDefinition, "sources" | "sheets">,
  columns?: TableColumns,
): WorkbookTableItem[] {
  return [...definition.sources, ...definition.sheets].map(({ id, name }) => {
    const schema = columns?.get(id);
    return schema
      ? { id, name, columns: schema.map((column) => column.name) }
      : { id, name };
  });
}

/** The table a Table reference names. Names match ignoring case, as in Excel. */
export function resolveTable(
  items: readonly WorkbookTableItem[],
  table: TableName,
): TableResolution {
  if (table.kind === "id") {
    const item = items.find((entry) => entry.id === table.id);
    return item ? { ok: true, item } : { ok: false, reason: "missing" };
  }
  const name = table.name.toLowerCase();
  const [item, ...more] = items.filter(
    (entry) => entry.name.toLowerCase() === name,
  );
  if (!item) return { ok: false, reason: "unknown" };
  return more.length ? { ok: false, reason: "ambiguous" } : { ok: true, item };
}

/** The tables a Formula's Table references resolve to, each once, in order. */
export function formulaTableIds(
  expression: string,
  items: readonly WorkbookTableItem[],
): string[] {
  return [
    ...new Set(
      lexFormula(expression).flatMap((token) => {
        if (token.kind !== "table") return [];
        const resolved = resolveTable(items, token.table);
        return resolved.ok ? [resolved.item.id] : [];
      }),
    ),
  ];
}
